import { Clock, Info } from 'lucide-react';
import { type ReactNode } from 'react';

import { Tag } from '@/components/ui/tag';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';

interface TimeZoneChipProps {
  /** The chip's text, naming the zone the dates on screen count in. */
  children: ReactNode;
  /** The tooltip's title, read on hover or keyboard focus. */
  tooltipTitle: ReactNode;
  /** The tooltip's body: one or more paragraphs. */
  tooltipBody: ReactNode;
  /** Ends the chip with an info icon, where the design shows one. */
  withInfoIcon?: boolean;
}

/** A time zone label, with why it applies on hover or keyboard focus. */
export function TimeZoneChip({
  children,
  tooltipTitle,
  tooltipBody,
  withInfoIcon = false,
}: TimeZoneChipProps) {
  return (
    <Tooltip
      side="bottom"
      content={
        <div className="space-y-1">
          <p className="font-medium">{tooltipTitle}</p>
          {tooltipBody}
        </div>
      }
    >
      <button
        type="button"
        className="max-w-full cursor-help rounded-full text-left"
        data-testid="time-zone-chip"
      >
        {/* Common zones fit on one line; a longer one wraps inside its column. */}
        <Tag
          className="border-fx-neutral-200 h-auto min-h-6 max-w-full border bg-white whitespace-normal"
          textClassName="min-w-0"
          icon={<Clock aria-hidden className="text-fx-neutral-500 size-3" />}
          text={
            <>
              {children}
              {withInfoIcon && (
                <Info
                  aria-hidden
                  className="text-fx-neutral-500 ml-1.5 inline size-3 align-[-2px]"
                />
              )}
            </>
          }
        />
      </button>
    </Tooltip>
  );
}

/** The zone inside a chip's text, set apart from the words around it. */
export function TimeZoneChipZone({ children }: { children: ReactNode }) {
  return (
    <Typography as="span" variant="small2" className="whitespace-nowrap">
      {children}
    </Typography>
  );
}
